import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import {
  PROJECT_PAN_LIMIT,
  PROJECT_TOUCH_SENSITIVITY,
} from './projectViewConfig';

const GESTURE_THRESHOLD = 6;
const SENSOR_ROTATION_SENSITIVITY = 0.012;
const SENSOR_Z_SENSITIVITY = 0.025;

/**
 * Horizontal-only project panning for touch devices.
 * Vertical gestures remain available to the browser for page scrolling.
 */
const TouchPanControls = ({ motionEnabled }) => {
  const { camera, gl } = useThree();
  const touchStartRef = useRef({ x: 0, y: 0 });
  const startRotationRef = useRef(0);
  const targetRotationRef = useRef(0);
  const targetZRef = useRef(8.2);
  const lockedPitchRef = useRef(0);
  const isDraggingRef = useRef(false);
  const gestureDirectionRef = useRef(null);
  const sensorOriginRef = useRef(null);

  useEffect(() => {
    targetRotationRef.current = THREE.MathUtils.clamp(
      camera.rotation.y,
      -PROJECT_PAN_LIMIT,
      PROJECT_PAN_LIMIT
    );
    startRotationRef.current = targetRotationRef.current;
    targetZRef.current = camera.position.z;
    lockedPitchRef.current = camera.rotation.x;
  }, [camera]);

  useFrame((_, delta) => {
    camera.rotation.y = THREE.MathUtils.clamp(
      THREE.MathUtils.damp(
        camera.rotation.y,
        targetRotationRef.current,
        8,
        delta
      ),
      -PROJECT_PAN_LIMIT,
      PROJECT_PAN_LIMIT
    );

    // Lock pitch completely on phones so vertical swipes never tilt the scene.
    camera.rotation.x = lockedPitchRef.current;
    camera.position.z = THREE.MathUtils.damp(
      camera.position.z,
      targetZRef.current,
      6,
      delta
    );
  });

  useEffect(() => {
    sensorOriginRef.current = null;
    if (!motionEnabled) {
      targetZRef.current = 8.2;
      return undefined;
    }

    const handleOrientation = (event) => {
      if (
        isDraggingRef.current
        || event.gamma === null
        || event.beta === null
      ) {
        return;
      }

      if (!sensorOriginRef.current) {
        sensorOriginRef.current = {
          gamma: event.gamma,
          beta: event.beta,
        };
      }

      const gammaDelta = event.gamma - sensorOriginRef.current.gamma;
      const betaDelta = event.beta - sensorOriginRef.current.beta;

      targetRotationRef.current = THREE.MathUtils.clamp(
        -gammaDelta * SENSOR_ROTATION_SENSITIVITY,
        -PROJECT_PAN_LIMIT,
        PROJECT_PAN_LIMIT
      );
      targetZRef.current = 8.2 + THREE.MathUtils.clamp(
        betaDelta * SENSOR_Z_SENSITIVITY,
        -0.75,
        0.75
      );
    };

    window.addEventListener('deviceorientation', handleOrientation, true);
    return () => {
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  }, [motionEnabled]);

  useEffect(() => {
    const canvas = gl.domElement;

    const handleTouchStart = (event) => {
      if (event.touches.length !== 1) return;

      isDraggingRef.current = true;
      gestureDirectionRef.current = null;
      touchStartRef.current = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
      startRotationRef.current = targetRotationRef.current;
    };

    const handleTouchMove = (event) => {
      if (!isDraggingRef.current || event.touches.length !== 1) return;

      const deltaX = event.touches[0].clientX - touchStartRef.current.x;
      const deltaY = event.touches[0].clientY - touchStartRef.current.y;

      if (!gestureDirectionRef.current) {
        if (
          Math.abs(deltaX) < GESTURE_THRESHOLD
          && Math.abs(deltaY) < GESTURE_THRESHOLD
        ) {
          return;
        }

        gestureDirectionRef.current = Math.abs(deltaX) > Math.abs(deltaY)
          ? 'horizontal'
          : 'vertical';
      }

      if (gestureDirectionRef.current !== 'horizontal') return;

      if (event.cancelable) event.preventDefault();

      targetRotationRef.current = THREE.MathUtils.clamp(
        startRotationRef.current + deltaX * PROJECT_TOUCH_SENSITIVITY,
        -PROJECT_PAN_LIMIT,
        PROJECT_PAN_LIMIT
      );
    };

    const handleTouchEnd = () => {
      isDraggingRef.current = false;
      gestureDirectionRef.current = null;
      startRotationRef.current = targetRotationRef.current;
    };

    canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: true });
    canvas.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    canvas.style.touchAction = 'pan-y';

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [gl]);

  return null;
};

export default TouchPanControls;
